import {
    getStringAttribute,
    setStringAttribute,
    upgradeProperty,
} from "./searchable-picker-core.js";
import {
    noticeDisclosureChangeDetail,
    optionalNoticeDisclosureId,
    parseNoticeDisclosureProjectionV1,
} from "../notice-disclosure-contract.js";

const DEFAULT_SUMMARY_LABEL = "Notice";
const DEFAULT_ICON = "alert-triangle";
const DEFAULT_BODY_CLASS = "space-y-3 px-1 pb-1 pt-3";
const DETAILS_CLASS = "w-full rounded-box bg-base-200/45 p-2";
const SUMMARY_CLASS = "flex w-full items-center justify-between gap-3";
const SUMMARY_PREFIX_CLASS = "inline-flex items-center gap-2 leading-none";
const SUMMARY_TITLE_CLASS = "font-medium leading-none";
const ICON_CLASS = "fishy-icon size-[1.125rem] shrink-0 self-center opacity-70";
const STATUS_CLASS = "ml-auto text-xs leading-tight";
const HTMLElementBase = globalThis.HTMLElement ?? class {};

function createSvgUse(icon) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", ICON_CLASS);
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");

    const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
    use.setAttribute("width", "100%");
    use.setAttribute("height", "100%");
    use.setAttribute("href", `#fishy-${icon}`);
    svg.append(use);

    return svg;
}

function appendChildNodes(target, nodes) {
    for (const node of nodes) {
        target.appendChild(node);
    }
}

export class FishyNoticeDisclosure extends HTMLElementBase {
    static get observedAttributes() {
        return ["body-class", "icon", "notice-id", "open", "summary-label"];
    }

    constructor() {
        super();
        this._contentElement = null;
        this._detailsElement = null;
        this._iconUseElement = null;
        this._statusElement = null;
        this._titleElement = null;
        this._defaultOpen = null;
        this._preferenceProjection = null;
        this._suppressOpenReflection = false;

        this._handleToggle = this._handleToggle.bind(this);
    }

    get bodyClass() {
        return getStringAttribute(this, "body-class");
    }

    set bodyClass(value) {
        setStringAttribute(this, "body-class", value);
    }

    get icon() {
        return getStringAttribute(this, "icon") || DEFAULT_ICON;
    }

    set icon(value) {
        setStringAttribute(this, "icon", value || DEFAULT_ICON);
    }

    get noticeId() {
        return getStringAttribute(this, "notice-id");
    }

    set noticeId(value) {
        setStringAttribute(this, "notice-id", value);
    }

    get defaultOpen() {
        return this._defaultOpen ?? this.hasAttribute("open");
    }

    get preferenceProjection() {
        return this._preferenceProjection;
    }

    set preferenceProjection(value) {
        if (value === null || value === undefined) {
            this._preferenceProjection = null;
        } else {
            const projection = parseNoticeDisclosureProjectionV1(value);
            const noticeId = optionalNoticeDisclosureId(this.noticeId);
            if (noticeId !== projection.noticeId) {
                throw new TypeError("Notice disclosure projection targets another notice.");
            }
            this._preferenceProjection = projection;
        }
        this._syncUi();
    }

    get open() {
        return this.hasAttribute("open");
    }

    set open(value) {
        if (value) {
            this.setAttribute("open", "");
            return;
        }
        this.removeAttribute("open");
    }

    get summaryLabel() {
        return getStringAttribute(this, "summary-label") || DEFAULT_SUMMARY_LABEL;
    }

    set summaryLabel(value) {
        setStringAttribute(this, "summary-label", value || DEFAULT_SUMMARY_LABEL);
    }

    connectedCallback() {
        upgradeProperty(this, "bodyClass");
        upgradeProperty(this, "icon");
        upgradeProperty(this, "noticeId");
        upgradeProperty(this, "open");
        upgradeProperty(this, "preferenceProjection");
        upgradeProperty(this, "summaryLabel");
        if (this._defaultOpen === null) {
            this._defaultOpen = this.hasAttribute("open");
        }
        this._ensureShell();
        if (this._detailsElement) {
            this._detailsElement.removeEventListener("toggle", this._handleToggle);
            this._detailsElement.addEventListener("toggle", this._handleToggle);
        }
        this._syncUi();
        this._requestPreferenceProjection();
    }

    disconnectedCallback() {
        this._detailsElement?.removeEventListener("toggle", this._handleToggle);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }
        if (name === "notice-id") {
            this._preferenceProjection = null;
            this._requestPreferenceProjection();
        }
        if (name === "open" && this._suppressOpenReflection) {
            return;
        }
        if (name === "open" && this._detailsElement) {
            const shouldOpen = this.open;
            if (this._detailsElement.open !== shouldOpen) {
                this._detailsElement.open = shouldOpen;
            }
        }
        this._syncUi();
    }

    _ensureShell() {
        if (
            this._detailsElement
            && this._contentElement
            && this._titleElement
            && this._iconUseElement
            && this._statusElement
        ) {
            return;
        }

        const preservedChildren = Array.from(this.childNodes);

        const menu = document.createElement("ul");
        menu.className = "menu menu-sm w-full bg-transparent p-0";
        menu.dataset.fishyNoticeRoot = "";

        const item = document.createElement("li");
        item.className = "w-full";

        const details = document.createElement("details");
        details.className = DETAILS_CLASS;
        details.addEventListener("toggle", this._handleToggle);

        const summary = document.createElement("summary");
        summary.className = SUMMARY_CLASS;

        const prefix = document.createElement("span");
        prefix.className = SUMMARY_PREFIX_CLASS;

        const iconSvg = createSvgUse(this.icon);
        const iconUse = iconSvg.querySelector("use");
        const title = document.createElement("span");
        title.className = SUMMARY_TITLE_CLASS;
        const status = document.createElement("span");
        status.className = STATUS_CLASS;
        status.setAttribute("aria-live", "polite");
        status.hidden = true;

        prefix.append(iconSvg);
        prefix.append(title);
        summary.append(prefix);
        summary.append(status);

        const content = document.createElement("div");

        details.append(summary);
        details.append(content);
        item.append(details);
        menu.append(item);

        this.replaceChildren(menu);
        appendChildNodes(content, preservedChildren);

        this._contentElement = content;
        this._detailsElement = details;
        this._iconUseElement = iconUse;
        this._statusElement = status;
        this._titleElement = title;
    }

    _resolvedOpenState() {
        if (typeof this._preferenceProjection?.open === "boolean") {
            return this._preferenceProjection.open;
        }
        if (this._preferenceProjection) {
            return Boolean(this.defaultOpen);
        }
        return this.open;
    }

    _reflectOpenAttribute(open) {
        this._suppressOpenReflection = true;
        if (open) {
            this.setAttribute("open", "");
        } else {
            this.removeAttribute("open");
        }
        this._suppressOpenReflection = false;
    }

    _requestPreferenceProjection() {
        if (!this.isConnected) return;
        try {
            if (optionalNoticeDisclosureId(this.noticeId) === null) return;
        } catch (_error) {
            this._syncPersistenceStatus("invalidNoticeId");
            return;
        }
        this.dispatchEvent(new CustomEvent("fishy-notice-disclosure-ready", {
            bubbles: true,
            composed: true,
        }));
    }

    _handleToggle() {
        const details = this._detailsElement;
        if (!details || typeof details.open !== "boolean") {
            return;
        }
        this._reflectOpenAttribute(details.open);
        if (
            typeof this._preferenceProjection?.open === "boolean"
            && this._preferenceProjection.open === details.open
        ) {
            return;
        }
        let noticeId;
        try {
            noticeId = optionalNoticeDisclosureId(this.noticeId);
        } catch (_error) {
            this._syncPersistenceStatus("invalidNoticeId");
            return;
        }
        if (!noticeId || this._preferenceProjection?.canPersist === false) return;
        const detail = noticeDisclosureChangeDetail({
            noticeId,
            open: details.open,
            expectedRevision: this._preferenceProjection?.revision ?? null,
        });
        this.dispatchEvent(new CustomEvent("fishy-notice-disclosure-change", {
            bubbles: true,
            composed: true,
            detail,
        }));
    }

    _syncPersistenceStatus(overrideKind = null) {
        if (!this._statusElement) return;
        const kind = overrideKind ?? this._preferenceProjection?.kind ?? null;
        let message = "";
        let tone = "text-base-content/60";
        if (kind === "pending" || kind === "loading") {
            message = kind === "pending" ? "Saving choice…" : "Loading saved choice…";
        } else if (kind === "conflict") {
            message = "Choice changed elsewhere. Review and try again.";
            tone = "text-warning";
        } else if (
            kind === "invalid"
            || kind === "invalidNoticeId"
            || kind === "unsupportedVersion"
            || kind === "unavailable"
        ) {
            message = "Saved choice unavailable.";
            tone = "text-error";
        } else if (this._preferenceProjection?.warning === "legacyCleanupUnavailable") {
            message = "Choice saved; old setting cleanup is pending.";
            tone = "text-warning";
        }
        this._statusElement.textContent = message;
        this._statusElement.hidden = message === "";
        this._statusElement.className = `${STATUS_CLASS} ${tone}`;
    }

    _syncUi() {
        if (
            !this._contentElement
            || !this._detailsElement
            || !this._titleElement
            || !this._iconUseElement
            || !this._statusElement
        ) {
            return;
        }
        this._contentElement.className = this.bodyClass || DEFAULT_BODY_CLASS;
        const resolvedOpen = this._resolvedOpenState();
        this._detailsElement.open = resolvedOpen;
        this._reflectOpenAttribute(resolvedOpen);
        this._iconUseElement.setAttribute("href", `#fishy-${this.icon}`);
        this._titleElement.textContent = this.summaryLabel;
        this._syncPersistenceStatus();
    }
}

export function registerNoticeDisclosure() {
    if (window.customElements.get("fishy-notice-disclosure")) {
        return;
    }

    window.customElements.define("fishy-notice-disclosure", FishyNoticeDisclosure);
}
